import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ChevronsUpDown, X, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Iter60.p15 / p15.1 — Shared employee picker for Leave / Discipline / Clothing & PPE.
 *
 * ## What changed in p15.1
 *  1. Server-side search — the picker now calls
 *     `GET /api/employees?module_id=<moduleId>&search=<q>&status=active&page_size=200`
 *     on every keystroke (debounced 250ms). This closes the "200-employee
 *     client-side cap" edge case where a large module could hide people.
 *     Scope enforcement lives on the backend (see `_build_employee_query`
 *     in `employees.py` — `module_scope_filter(user)` for Dept Mgrs +
 *     `can_access_module(user, module_id)` for explicit param — so scope
 *     CANNOT be bypassed through this endpoint).
 *  2. Fallback display when `module_employee_id` is null:
 *       - With Module ID:    "ACS019 — Mahad Abdulkadir Mudey Maow (EMP-4334)"
 *       - Without Module ID: "EMP-4334 — Mahad Abdulkadir Mudey Maow"
 *     Never renders "— — Name (Code)" or an em-dash-to-empty.
 *  3. `employees` prop kept for backwards-compat / callers that want to
 *     provide a pre-loaded list (e.g. Discipline edit mode where we need
 *     to display the immutable selected employee). If `moduleId` is
 *     supplied, we own the fetch.
 *
 * ## Behavior (unchanged from p15)
 *  - No auto-select on Enter / blur. `highlight` starts at -1; Enter is
 *    a no-op until the user explicitly moves via ArrowDown / ArrowUp.
 *  - Selection is committed by click, or by Enter on a manually-highlighted
 *    row. Blur closes without commit.
 *  - Testids follow `{picker-name}-item-{employee_code}` — see the map at
 *    the bottom of the p15.1 report.
 */
export default function EmployeePicker({
    // Either supply a static list …
    employees,
    // … or supply a moduleId and we'll fetch it (recommended path).
    moduleId,
    value,
    onChange,
    placeholder = "Select employee…",
    disabled = false,
    testid = "employee-picker",
    emptyLabel = "No employees match this search.",
    // Optional — restrict to specific employment statuses. Defaults to
    // "active" so terminated/on-hold folks don't clutter the picker.
    statusFilter = "active",
    // Iter60.p47 Item A — Optional predicate to exclude employees
    // (e.g. casuals in the Leave dialog). Runs client-side after the
    // server fetch. Return `false` to hide the row.
    filter = null,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    // -1 means "no highlight" (initial state). Enter on -1 does NOTHING
    // (no auto-select), matching option-C behavior.
    const [highlight, setHighlight] = useState(-1);
    const [fetched, setFetched] = useState(null);
    const [loading, setLoading] = useState(false);
    const listRef = useRef(null);
    const inputRef = useRef(null);
    const debounceTimer = useRef(null);
    const fetchToken = useRef(0);
    // p15.1 — keep the last-selected employee cached so the trigger can
    // render its label even after the popover closes and clears `fetched`.
    const [selectedCache, setSelectedCache] = useState(null);

    // Decide whether to own the fetch (moduleId supplied) or read from prop.
    const ownsFetch = !!moduleId;

    // p15.1 — server-side search when `moduleId` is set.
    // Fires on open + on query change, debounced.
    useEffect(() => {
        if (!ownsFetch || !open) return;
        // Cancel any pending timer.
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const q = query.trim();
        const token = ++fetchToken.current;
        debounceTimer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = {
                    module_id: moduleId,
                    page_size: 200,
                };
                if (statusFilter) params.status = statusFilter;
                if (q) params.search = q;
                const { data } = await api.get("/employees", { params });
                // Guard against out-of-order responses.
                if (token !== fetchToken.current) return;
                const items = data?.items ?? data ?? [];
                setFetched(items);
                // Reset highlight only when the list actually changes.
                setHighlight(-1);
            } catch {
                if (token === fetchToken.current) {
                    setFetched([]);
                }
            } finally {
                if (token === fetchToken.current) setLoading(false);
            }
        }, 250);
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [ownsFetch, open, query, moduleId, statusFilter]);

    // Reset local state when the popover closes.
    useEffect(() => {
        if (!open) {
            setQuery("");
            setHighlight(-1);
            if (ownsFetch) setFetched(null);
        }
    }, [open, ownsFetch]);

    // p15.1 — resolve the selected row for the trigger label.
    // Priority: (a) currently-fetched list, (b) `employees` prop,
    // (c) previously-committed cache (survives popover close).
    const selected = useMemo(() => {
        if (!value) return null;
        const listA = ownsFetch ? (fetched || []) : (employees || []);
        const inFetched = listA.find((e) => e.id === value);
        if (inFetched) return inFetched;
        if (selectedCache && selectedCache.id === value) return selectedCache;
        const inProp = (employees || []).find((e) => e.id === value);
        return inProp || null;
    }, [value, fetched, employees, ownsFetch, selectedCache]);

    // Client-side filter path (only when picker doesn't own the fetch).
    const clientFiltered = useMemo(() => {
        if (ownsFetch) return null;
        const q = query.trim().toLowerCase();
        const list = employees || [];
        if (!q) return list;
        // Iter60.p49 Item C — `employee_code` (EMP-####) dropped from
        // the client-side search predicate to prevent EMP-####
        // false-positive matches when the user is typing a Module ID.
        return list.filter((e) => {
            const mid = (e.module_employee_id || "").toLowerCase();
            const name = (e.full_name || "").toLowerCase();
            return mid.includes(q) || name.includes(q);
        });
    }, [employees, query, ownsFetch]);

    const visible = ownsFetch ? (fetched || []) : (clientFiltered || []);
    // Iter60.p47 Item A — apply optional caller-provided predicate
    // (used to hide casual workers from the Leave dialog).
    const filteredVisible = useMemo(
        () => (typeof filter === "function" ? visible.filter(filter) : visible),
        [visible, filter],
    );

    // Ensure highlight stays within range when the visible list shrinks.
    useEffect(() => {
        if (highlight >= filteredVisible.length) setHighlight(-1);
    }, [filteredVisible.length, highlight]);

    const commit = (emp) => {
        if (!emp) return;
        setSelectedCache(emp);
        onChange?.(emp.id);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(filteredVisible.length - 1, h + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(-1, h - 1));
        } else if (e.key === "Enter") {
            // p15 — do NOT auto-select on Enter unless the user has
            // explicitly moved the highlight to an item via ArrowDown.
            if (highlight >= 0 && highlight < filteredVisible.length) {
                e.preventDefault();
                commit(filteredVisible[highlight]);
            }
            // If highlight === -1, Enter is a no-op.
        } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
            <PopoverTrigger asChild disabled={disabled}>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className="w-full justify-between min-h-9 h-auto py-1.5"
                    data-testid={`${testid}-trigger`}
                >
                    {selected ? (
                        <EmployeeLine emp={selected} testid={`${testid}-selected`} />
                    ) : (
                        <span className="text-[color:var(--asl-muted)] text-sm">{placeholder}</span>
                    )}
                    <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2 flex-shrink-0" aria-hidden="true" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0 w-[420px] max-w-[90vw]"
                align="start"
                sideOffset={4}
                onOpenAutoFocus={(e) => {
                    // Focus the input so typing works immediately.
                    e.preventDefault();
                    inputRef.current?.focus();
                }}
                data-testid={`${testid}-popover`}
            >
                <div className="p-2 border-b border-[color:var(--asl-border)]">
                    <div className="relative">
                        <Input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setHighlight(-1); }}
                            onKeyDown={onKeyDown}
                            placeholder="Search by Module ID, name, or EMP Code…"
                            className="h-9 pr-8"
                            autoComplete="off"
                            spellCheck={false}
                            data-testid={`${testid}-search`}
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => { setQuery(""); setHighlight(-1); inputRef.current?.focus(); }}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                                aria-label="Clear search"
                                data-testid={`${testid}-clear`}
                            >
                                <X className="h-3.5 w-3.5 text-[color:var(--asl-muted)]" />
                            </button>
                        )}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-[color:var(--asl-muted)]" data-testid={`${testid}-hint`}>
                        {loading && (
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                        )}
                        {loading ? "Searching…" : (
                            <>{filteredVisible.length} match{filteredVisible.length === 1 ? "" : "es"} · click to select</>
                        )}
                    </div>
                </div>
                <div
                    ref={listRef}
                    className="max-h-[280px] overflow-y-auto py-1"
                    role="listbox"
                    data-testid={`${testid}-list`}
                >
                    {filteredVisible.length === 0 ? (
                        <div
                            className="px-3 py-6 text-center text-xs text-[color:var(--asl-muted)]"
                            data-testid={`${testid}-empty`}
                        >
                            {loading ? " " : emptyLabel}
                        </div>
                    ) : (
                        filteredVisible.map((e, idx) => {
                            const isHighlighted = idx === highlight;
                            const isSelected = e.id === value;
                            return (
                                <button
                                    key={e.id}
                                    type="button"
                                    onClick={() => commit(e)}
                                    onMouseEnter={() => setHighlight(idx)}
                                    className={
                                        "w-full text-left px-3 py-2 flex items-center justify-between gap-2 "
                                        + (isHighlighted ? "bg-slate-100 " : "hover:bg-slate-50 ")
                                    }
                                    role="option"
                                    aria-selected={isSelected}
                                    data-testid={`${testid}-item-${e.employee_code}`}
                                    data-highlighted={isHighlighted ? "true" : "false"}
                                >
                                    <EmployeeLine emp={e} testid={`${testid}-item-${e.employee_code}-line`} />
                                    {isSelected && (
                                        <Check className="h-4 w-4 asl-crimson-text flex-shrink-0" aria-hidden="true" />
                                    )}
                                </button>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

/**
 * p15.1 — Canonical employee display line with fallback for missing IDs.
 *
 * Rendering rules (in priority order):
 *   1. Both module_employee_id AND employee_code set:
 *        [ACS019] — Full Name (EMP-4334)
 *        └ bold navy 14px            └ mono 10px muted
 *   2. Only employee_code (no module_employee_id):
 *        [EMP-4334] — Full Name
 *        └ bold navy 14px  (EMP code takes primary slot)
 *   3. Only module_employee_id (no employee_code — unusual):
 *        [ACS019] — Full Name
 *   4. Neither (shouldn't happen; guard for it):
 *        Full Name
 *
 * Never renders `— — {Name}` or `{Name} ()` — the em-dash + parens
 * disappear cleanly when the secondary field is missing.
 */
export function EmployeeLine({ emp, testid }) {
    if (!emp) return null;
    const modId = (emp.module_employee_id || "").trim();
    const empCode = (emp.employee_code || "").trim();
    const primary = modId || empCode || null;
    // Only show a distinct secondary when the primary was Module ID
    // AND the EMP code exists (rule 1). Otherwise the secondary is
    // absorbed into primary (rules 2, 3) and we skip the trailing parens.
    const secondary = modId && empCode ? empCode : null;

    return (
        <span
            className="flex items-center gap-2 min-w-0"
            data-testid={testid}
            data-has-module-id={modId ? "true" : "false"}
        >
            {primary ? (
                <>
                    <span
                        className="mono font-bold text-sm asl-navy-text flex-shrink-0"
                        data-testid={`${testid}-primary`}
                    >
                        {primary}
                    </span>
                    <span className="text-[color:var(--asl-muted)]" aria-hidden="true">—</span>
                </>
            ) : null}
            <span
                className="text-sm truncate"
                data-testid={`${testid}-name`}
            >
                {emp.full_name || "—"}
            </span>
            {secondary && (
                <span
                    className="mono text-[10px] text-[color:var(--asl-muted)] flex-shrink-0"
                    data-testid={`${testid}-secondary`}
                >
                    ({secondary})
                </span>
            )}
        </span>
    );
}
